import axios from "axios";
import logo from "../data/images/GPML.png";
import { deleteAccount } from "../api/user";
import { useParams } from "react-router-dom";
import { useState, useEffect, lazy } from "react";
import { useMutation } from "@tanstack/react-query";
import * as endpoints from "../data/links/endpoints";

const Stack = lazy(() => import("@mui/material/Stack"));

// ─────────────────────────────────────────
// Theme
// ─────────────────────────────────────────
const C = {
  bg: "#0a0a0a",
  surface: "#111111",
  card: "#161616",
  border: "#1e1e1e",
  borderLight: "#252525",
  orange: "#f97316",
  orangeDark: "#ea580c",
  text: "#f5f5f5",
  textSub: "#b0b0b0",
  muted: "#555",
  dim: "#333",
  up: "#22d47b",
  down: "#ff3b5c",
};

const DeleteAccount = () => {
  useKeyframes();
  const { token } = useParams();
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    axios
      .get(endpoints.LOGOUT_USER)
      .then(() => console.log("Logged out"))
      .catch((error) => console.log(error))
      .finally(() => mutate({ token: token }));
  }, []);

  const { isPending, mutate, isError } = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => setDeleted(true),
    onError: () => setDeleted(false),
  });

  let content;
  if (isPending) {
    content = <LoadingState />;
  }

  if (deleted) content = <SuccessState />;

  if (isError) content = <ErrorState />;

  return <Stack height={"100vh"}>{content}</Stack>;
};
export default DeleteAccount;

// ─────────────────────────────────────────
// ── STATE 1: LOADING ──
// ─────────────────────────────────────────
function LoadingState() {
  return (
    <PageShell
      glowColor="rgba(249,115,22,.1)"
      glow2Color="rgba(249,115,22,.04)"
    >
      <NavBar />
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 32,
          }}
        >
          {/* Spinner ring */}
          <div style={{ position: "relative", width: 88, height: 88 }}>
            {/* Outer pulse ring */}
            <div
              style={{
                position: "absolute",
                inset: -12,
                borderRadius: "50%",
                border: "1px solid rgba(249,115,22,.2)",
                animation: "gpRing 2s ease-out infinite",
              }}
            />
            {/* Secondary pulse */}
            <div
              style={{
                position: "absolute",
                inset: -6,
                borderRadius: "50%",
                border: "1px solid rgba(249,115,22,.15)",
                animation: "gpRing 2s ease-out .6s infinite",
              }}
            />
            {/* GP mark */}
            <img
              src={logo}
              alt={"GP Logo"}
              style={{ width: 88, height: 88, borderRadius: "50%" }}
            />
            {/* Spinner arc */}
            <svg
              style={{
                position: "absolute",
                inset: -6,
                width: 100,
                height: 100,
                animation: "gpSpin 1s linear infinite",
              }}
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke={C.orange}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="72 216"
                opacity="0.8"
              />
            </svg>
          </div>

          {/* Text */}
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: "Oswald, sans-serif",
                fontWeight: 700,
                fontSize: "clamp(22px,3vw,32px)",
                color: C.text,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Deleting Account
            </div>

            {/* Animated dots */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {[0, 160, 320].map((delay, i) => (
                <div
                  key={i}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: C.muted,
                    animation: `gpDot1 1.4s ease-in-out ${delay}ms infinite`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Subtext card */}
          <div
            style={{
              padding: "14px 24px",
              borderRadius: 14,
              background: C.surface,
              border: `1px solid ${C.border}`,
              fontFamily: "DM Sans, sans-serif",
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.6,
              textAlign: "center",
              maxWidth: 340,
            }}
          >
            Permanently removing your account, workout history, and all
            associated data. Please don't close this window.
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────
// Shared background shell
// ─────────────────────────────────────────
function PageShell({
  children,
  glowColor = C.orange,
  glow2Color = "rgba(249,115,22,.06)",
}) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        fontFamily: "DM Sans, sans-serif",
        color: C.text,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Ambient top glow */}
      <div
        style={{
          position: "fixed",
          top: -160,
          left: "50%",
          transform: "translateX(-50%)",
          width: 700,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(ellipse, ${glowColor} 0%, ${glow2Color} 40%, transparent 70%)`,
          pointerEvents: "none",
          zIndex: 0,
          animation: "gpAmbient 4s ease-in-out infinite alternate",
        }}
      />
      {/* Bottom accent */}
      <div
        style={{
          position: "fixed",
          bottom: -180,
          right: -80,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: `radial-gradient(ellipse, ${glow2Color} 0%, transparent 65%)`,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Grain */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          opacity: 0.2,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.04'/%3E%3C/svg%3E\")",
          backgroundSize: "200px 200px",
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Logo nav strip (top-left branding)
// ─────────────────────────────────────────
function NavBar() {
  return (
    <nav
      className="gp-fade-0"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "clamp(16px,3vw,28px) clamp(20px,5vw,60px)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <img
          src={logo}
          alt="GP Logo"
          style={{ width: 38, height: 38, borderRadius: "50%" }}
        />
        <div
          style={{
            fontFamily: "Oswald, sans-serif",
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Grind<span style={{ color: C.orange }}>&nbsp;Partner</span>
        </div>
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────
// Inject global keyframes + fonts once
// ─────────────────────────────────────────
function useKeyframes() {
  useEffect(() => {
    if (document.getElementById("gp-del-kf")) return;
    const s = document.createElement("style");
    s.id = "gp-del-kf";
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700;900&family=DM+Sans:wght@400;500;600;700&display=swap');
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: #f97316; border-radius: 2px; }

      /* Ambient glow */
      @keyframes gpAmbient { from{opacity:.55;transform:translateX(-50%) scale(1)} to{opacity:1;transform:translateX(-50%) scale(1.1)} }

      /* Spinner */
      @keyframes gpSpin { to { transform: rotate(360deg); } }

      /* Content entrance */
      @keyframes gpFadeUp  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
      @keyframes gpScaleIn { from{opacity:0;transform:scale(.6)} to{opacity:1;transform:scale(1)} }
      @keyframes gpSlideIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

      /* Success ring draw */
      @keyframes gpDraw    { from{stroke-dashoffset:200} to{stroke-dashoffset:0} }
      @keyframes gpCheckIn { from{stroke-dashoffset:50}  to{stroke-dashoffset:0} }
      @keyframes gpPop     { 0%{transform:scale(.5)} 60%{transform:scale(1.15)} 100%{transform:scale(1)} }

      /* Error shake */
      @keyframes gpShake   {
        0%,100%{transform:translateX(0)}
        20%{transform:translateX(-8px)}
        40%{transform:translateX(8px)}
        60%{transform:translateX(-5px)}
        80%{transform:translateX(5px)}
      }

      /* Pulse ring */
      @keyframes gpRing {
        0%   { transform:scale(1); opacity:.5; }
        100% { transform:scale(1.6); opacity:0; }
      }

      /* Loading dots */
      @keyframes gpDot1 { 0%,80%,100%{transform:scale(0);opacity:0} 40%{transform:scale(1);opacity:1} }
      @keyframes gpDot2 { 0%,100%{transform:scale(0);opacity:0} 40%{transform:scale(1);opacity:1} }

      /* Particle float */
      @keyframes gpFloat1 { 0%,100%{transform:translateY(0) rotate(0deg); opacity:.6} 50%{transform:translateY(-18px) rotate(15deg); opacity:1} }
      @keyframes gpFloat2 { 0%,100%{transform:translateY(0) rotate(0deg); opacity:.4} 50%{transform:translateY(-12px) rotate(-12deg); opacity:.7} }
      @keyframes gpFloat3 { 0%,100%{transform:translateY(0) rotate(0deg); opacity:.5} 50%{transform:translateY(-22px) rotate(8deg); opacity:.9} }

      .gp-fade-0 { animation: gpFadeUp .6s ease .05s both; }
      .gp-fade-1 { animation: gpFadeUp .6s ease .15s both; }
      .gp-fade-2 { animation: gpFadeUp .6s ease .30s both; }
      .gp-fade-3 { animation: gpFadeUp .6s ease .45s both; }
      .gp-fade-4 { animation: gpFadeUp .6s ease .60s both; }
      .gp-btn { transition: all .2s ease; cursor: pointer; }
      .gp-btn:hover { transform: translateY(-2px) !important; }
      .gp-btn:active { transform: translateY(0) !important; }
    `;
    document.head.appendChild(s);
  }, []);
}

// ─────────────────────────────────────────
// ── STATE 3: ERROR ──
// ─────────────────────────────────────────
function ErrorState() {
  return (
    <PageShell glowColor="rgba(255,59,92,.1)" glow2Color="rgba(255,59,92,.04)">
      <NavBar />
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 28,
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
          }}
        >
          {/* Error ring + X */}
          <div
            className="gp-fade-0"
            style={{
              position: "relative",
              width: 100,
              height: 100,
              animation: "gpShake .5s ease .2s both",
            }}
          >
            <svg width="100" height="100" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="47"
                fill="none"
                stroke="rgba(255,59,92,.15)"
                strokeWidth="1.5"
              />
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke={C.down}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="264"
                strokeDashoffset="264"
                style={{
                  transformOrigin: "center",
                  transform: "rotate(-90deg)",
                  animation: "gpDraw .7s cubic-bezier(.4,0,.2,1) .15s forwards",
                }}
              />
            </svg>
            {/* Inner glow */}
            <div
              style={{
                position: "absolute",
                inset: 12,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle,rgba(255,59,92,.16) 0%,rgba(255,59,92,.05) 60%,transparent 100%)",
                animation: "gpPop .5s cubic-bezier(.34,1.56,.64,1) .25s both",
              }}
            />
            {/* X icon */}
            <svg
              style={{
                position: "absolute",
                inset: 0,
                animation: "gpPop .45s cubic-bezier(.34,1.56,.64,1) .45s both",
                opacity: 0,
              }}
              viewBox="0 0 100 100"
            >
              <line
                x1="34"
                y1="34"
                x2="66"
                y2="66"
                stroke={C.down}
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="46"
                strokeDashoffset="46"
                style={{ animation: "gpCheckIn .35s ease .6s forwards" }}
              />
              <line
                x1="66"
                y1="34"
                x2="34"
                y2="66"
                stroke={C.down}
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="46"
                strokeDashoffset="46"
                style={{ animation: "gpCheckIn .35s ease .72s forwards" }}
              />
            </svg>
          </div>

          {/* Headline */}
          <div className="gp-fade-1">
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: C.down,
                marginBottom: 10,
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              Something Went Wrong
            </div>
            <h1
              style={{
                fontFamily: "Oswald, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(32px,5vw,56px)",
                lineHeight: 0.95,
                letterSpacing: "-0.01em",
                textTransform: "uppercase",
              }}
            >
              Deletion
              <br />
              <span
                style={{
                  WebkitTextFillColor: "transparent",
                  background: `linear-gradient(135deg, ${C.down} 20%, #ff6b81 80%)`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                }}
              >
                Failed
              </span>
            </h1>
          </div>

          {/* Body */}
          <p
            className="gp-fade-2"
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: "clamp(14px,1.3vw,16px)",
              color: C.textSub,
              lineHeight: 1.7,
              maxWidth: 400,
            }}
          >
            We couldn't complete the account deletion. This might be a temporary
            issue. Please try again or contact our support team if the problem
            persists.
          </p>

          {/* Error detail card */}
          <div
            className="gp-fade-3"
            style={{
              width: "100%",
              padding: "16px 20px",
              borderRadius: 14,
              background: "rgba(255,59,92,.06)",
              border: "1px solid rgba(255,59,92,.22)",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              textAlign: "left",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                flexShrink: 0,
                background: "rgba(255,59,92,.12)",
                border: "1px solid rgba(255,59,92,.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}
            >
              ⚠️
            </div>
            <div>
              <div
                style={{
                  fontFamily: "Oswald, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  color: C.down,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Error deleting your account
              </div>
              <div
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 12,
                  color: C.muted,
                  lineHeight: 1.55,
                }}
              >
                The request failed. Your account has{" "}
                <strong style={{ color: C.textSub }}>not</strong> been deleted.
                Please try again, or contact us if the issue continues.
              </div>
            </div>
          </div>

          {/* Actions */}
          <div
            className="gp-fade-4"
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <a
              href="mailto:grindpartnerinfo@gmail.com"
              className="gp-btn"
              style={{
                padding: "13px 24px",
                borderRadius: 12,
                border: `1px solid ${C.borderLight}`,
                background: C.card,
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                color: C.textSub,
                letterSpacing: "0.04em",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              ✉️ Contact Support
            </a>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────
// ── STATE 2: SUCCESS ──
// ─────────────────────────────────────────
function SuccessState() {
  // Floating particle positions
  const particles = [
    {
      emoji: "🏆",
      top: "18%",
      left: "12%",
      size: 28,
      anim: "gpFloat1 3.2s ease-in-out infinite",
    },
    {
      emoji: "💪",
      top: "22%",
      right: "10%",
      size: 24,
      anim: "gpFloat2 2.8s ease-in-out .4s infinite",
    },
    {
      emoji: "⚡",
      top: "72%",
      left: "8%",
      size: 22,
      anim: "gpFloat3 3.6s ease-in-out .2s infinite",
    },
    {
      emoji: "🎯",
      top: "68%",
      right: "9%",
      size: 26,
      anim: "gpFloat1 3s ease-in-out .6s infinite",
    },
    {
      emoji: "🔥",
      top: "42%",
      left: "4%",
      size: 20,
      anim: "gpFloat2 4s ease-in-out .1s infinite",
    },
    {
      emoji: "🥇",
      top: "38%",
      right: "5%",
      size: 22,
      anim: "gpFloat3 3.4s ease-in-out .8s infinite",
    },
  ];

  return (
    <PageShell
      glowColor="rgba(34,212,123,.13)"
      glow2Color="rgba(34,212,123,.05)"
    >
      {/* Floating particles */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: "fixed",
            fontSize: p.size,
            opacity: 0.35,
            top: p.top,
            left: p.left,
            right: p.right,
            animation: p.anim,
            pointerEvents: "none",
            zIndex: 0,
            userSelect: "none",
          }}
        >
          {p.emoji}
        </div>
      ))}

      <NavBar />
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 28,
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
          }}
        >
          {/* Success ring + checkmark */}
          <div
            className="gp-fade-0"
            style={{ position: "relative", width: 100, height: 100 }}
          >
            <svg width="100" height="100" viewBox="0 0 100 100">
              {/* Outer decorative ring */}
              <circle
                cx="50"
                cy="50"
                r="47"
                fill="none"
                stroke="rgba(34,212,123,0.15)"
                strokeWidth="1.5"
              />
              {/* Animated fill ring */}
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke={C.up}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="264"
                strokeDashoffset="264"
                style={{
                  transformOrigin: "center",
                  transform: "rotate(-90deg)",
                  animation: "gpDraw .8s cubic-bezier(.4,0,.2,1) .2s forwards",
                }}
              />
            </svg>
            {/* Inner fill */}
            <div
              style={{
                position: "absolute",
                inset: 12,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle,rgba(34,212,123,.18) 0%,rgba(34,212,123,.06) 60%,transparent 100%)",
                animation: "gpPop .5s cubic-bezier(.34,1.56,.64,1) .3s both",
              }}
            />
            {/* Check icon */}
            <svg
              style={{
                position: "absolute",
                inset: 0,
                animation: "gpPop .45s cubic-bezier(.34,1.56,.64,1) .55s both",
                opacity: 0,
              }}
              viewBox="0 0 100 100"
            >
              <polyline
                points="28,52 44,68 72,36"
                fill="none"
                stroke={C.up}
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="60"
                strokeDashoffset="60"
                style={{ animation: "gpCheckIn .4s ease .7s forwards" }}
              />
            </svg>
          </div>
          {/* Headline */}
          <div className="gp-fade-1">
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: C.up,
                marginBottom: 10,
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              Account Removed
            </div>
            <h1
              style={{
                fontFamily: "Oswald, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(32px,5vw,56px)",
                lineHeight: 0.95,
                letterSpacing: "-0.01em",
                textTransform: "uppercase",
                marginBottom: 0,
              }}
            >
              Successfully
              <br />
              <span
                style={{
                  WebkitTextFillColor: "transparent",
                  background: `linear-gradient(135deg, ${C.up} 20%, #4ade80 80%)`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                }}
              >
                Deleted
              </span>
            </h1>
          </div>
          {/* Body */}
          <p
            className="gp-fade-2"
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: "clamp(14px,1.3vw,16px)",
              color: C.textSub,
              lineHeight: 1.7,
              maxWidth: 400,
            }}
          >
            Your Grind Partner account and all associated data have been
            permanently removed. We're sorry to see you go — you're always
            welcome back.
          </p>
          {/* Info strip */}
          <div
            className="gp-fade-3"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              width: "100%",
            }}
          >
            {[
              { icon: "🗑️", text: "Workout history & scores deleted" },
              { icon: "🏆", text: "Achievements & personal bests removed" },
              { icon: "📧", text: "Confirmation email sent to your address" },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "11px 16px",
                  borderRadius: 12,
                  background: C.card,
                  border: `1px solid ${C.border}`,
                }}
              >
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span
                  style={{
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: 13,
                    color: C.textSub,
                  }}
                >
                  {item.text}
                </span>
                <div
                  style={{
                    marginLeft: "auto",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "rgba(34,212,123,.15)",
                    border: "1px solid rgba(34,212,123,.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    color: C.up,
                  }}
                >
                  ✓
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
